import type {
  RequirementEvaluationResponse,
  RequirementLevel,
  RequirementStatus,
  RequirementSubjectType,
  RoleFitStatus,
  RoleRequirementResponse,
} from '../../api/types';
import { PROFICIENCY_COPY } from '../profile/factDisplay';

/**
 * Presentation vocabulary for Roles and Role Fit.
 *
 * ---------------------------------------------------------------------
 * WHAT THIS FILE IS ALLOWED TO DO
 *
 * Turn the domain's codes into the reader's words, and format a value the
 * API already sent. That is all.
 *
 * It evaluates nothing. There is no threshold comparison here, no scoring,
 * no weighting, no near-miss margin, no re-derivation of a status from a
 * value, and no rule that decides whether something counts. Every one of
 * those lives in `domain/src/rules/requirementEvaluation.ts` and
 * `domain/src/rules/roleFit.ts`, arrives already decided, and is displayed
 * exactly as it arrived. A second Role Fit engine growing quietly in the
 * browser is the specific failure this file is written to prevent, so if a
 * future change here starts comparing two numbers, that is the signal it
 * has gone wrong.
 *
 * ON LANGUAGE
 *
 * A gap is a comparison between a recorded value and a role's threshold.
 * It is not a criticism of a person, and the wording below is chosen so it
 * cannot be read as one — no "fails", "deficient", "weak", "underperforming".
 * `unknown` and `not_evaluated` are worded as absences of information,
 * because that is exactly what they are.
 * ---------------------------------------------------------------------
 */

/** docs/ROLE_FRAMEWORK.md §4.1's four states, plus the domain's fifth for
 * requirement types no engine can evaluate yet. */
export const REQUIREMENT_STATUS_COPY: Record<RequirementStatus, { label: string; meaning: string }> = {
  met: {
    label: 'Met',
    meaning: "The recorded value is at or above what the role asks for.",
  },
  partial: {
    label: 'Partial',
    meaning:
      'Below what the role asks for, but within the near-miss margin the system treats as close rather than short.',
  },
  gap: {
    label: 'Gap',
    meaning:
      'The recorded value is below what the role asks for, or the record is absent. A comparison against a threshold, not a judgement about the person.',
  },
  unknown: {
    label: 'Not known',
    meaning:
      'Nothing has been assessed or recorded for this, so there is nothing to compare. An absence of information, not a gap.',
  },
  not_evaluated: {
    label: 'Not evaluable',
    meaning:
      'Nothing currently known about this person feeds this requirement — for a Capability, none of its components has a currently-known value. Neither met nor unmet, and never a fabricated result.',
  },
};

/** Neither `unknown` nor `not_evaluated` counts toward the percentage, and
 * neither is a failure — this drives whether a row is drawn as an outcome
 * or as an absence. */
export function isAbsenceOfInformation(status: RequirementStatus): boolean {
  return status === 'unknown' || status === 'not_evaluated';
}

export const SUBJECT_TYPE_COPY: Record<RequirementSubjectType, { label: string; plural: string }> = {
  attribute: { label: 'Attribute', plural: 'Attributes' },
  skill: { label: 'Skill', plural: 'Skills' },
  qualification: { label: 'Qualification', plural: 'Qualifications' },
  experience_area: { label: 'Experience', plural: 'Experience' },
  capability: { label: 'Capability', plural: 'Capabilities' },
};

/** The order categories are presented in — the same order the profile
 * presents the corresponding sections, so a reader meets them consistently. */
export const SUBJECT_TYPE_ORDER: RequirementSubjectType[] = [
  'attribute',
  'skill',
  'experience_area',
  'qualification',
  'capability',
];

export const REQUIREMENT_LEVEL_COPY: Record<RequirementLevel, { label: string; meaning: string }> = {
  required: { label: 'Required', meaning: 'An unmet required item is what makes a fit result carry a critical gap.' },
  preferred: { label: 'Preferred', meaning: 'Contributes to the overall figure but never creates a critical gap.' },
};

export const ROLE_FIT_STATUS_COPY: Record<RoleFitStatus, string> = {
  high: 'High',
  moderate: 'Moderate',
  low: 'Low',
};

/**
 * The domain's `reason` codes, in words.
 *
 * These are translations, not explanations of our own: each one restates
 * what `requirementEvaluation.ts` recorded. An unrecognised code falls back
 * to itself rather than being hidden or guessed at, so a future reason
 * added in the domain shows up as a visible untranslated code instead of
 * silently disappearing.
 */
const REASON_COPY: Record<string, string> = {
  meets_threshold: 'At or above the threshold',
  near_miss: 'Below the threshold but within the near-miss margin',
  below_threshold: 'Below the threshold',
  no_assessment: 'No assessment recorded for this attribute',
  no_skill_record: 'No record of this skill either way',
  missing: 'No record held',
  expired: 'Held, but past its recorded expiry date',
  insufficient_years: 'Fewer recorded years than the threshold',
  no_threshold_defined: 'The role records no threshold to compare against',
  held: 'Held',
  no_components_currently_assessed: 'None of this capability’s components currently has a known value',
};

export function reasonLabel(reason: string): string {
  return REASON_COPY[reason] ?? reason;
}

/**
 * What the role asks for, as text. A straight rendering of whichever
 * threshold field matches the subject type — no comparison, no arithmetic.
 * A qualification requirement genuinely has no threshold: it is held or it
 * is not, so it says so rather than showing a blank.
 */
export function describeThreshold(
  requirement: Pick<
    RoleRequirementResponse,
    'subjectType' | 'thresholdRating' | 'thresholdProficiencyLevel' | 'thresholdMinYears'
  >
): string {
  switch (requirement.subjectType) {
    case 'attribute':
      return requirement.thresholdRating === null
        ? 'No threshold recorded'
        : `${requirement.thresholdRating} or above, on the 1–20 scale`;
    case 'skill':
      return requirement.thresholdProficiencyLevel === null
        ? 'No proficiency recorded'
        : `${PROFICIENCY_COPY[requirement.thresholdProficiencyLevel].label} proficiency or above`;
    case 'experience_area':
      return requirement.thresholdMinYears === null
        ? 'No minimum recorded'
        : `${requirement.thresholdMinYears} ${requirement.thresholdMinYears === 1 ? 'year' : 'years'} or more recorded`;
    case 'qualification':
      return 'Held and not expired';
    case 'capability':
      // Capability shares Attribute's threshold field and 1–20 scale — its
      // computed value is deliberately rescaled onto it (Mission 166's
      // `computeCapabilityValue`) so the two are comparable the same way.
      return requirement.thresholdRating === null
        ? 'No threshold recorded'
        : `${requirement.thresholdRating} or above, on the 1–20 scale`;
  }
}

/**
 * What the employee has, as text — the value the domain compared, restated.
 *
 * The experience case deserves its own note. `experienceYears` is the sum
 * of the employee's recorded durations **for this one area**, and it is
 * overlap-unaware (docs/DERIVED_LOGIC.md §21). It is shown here because it
 * is what this comparison was made against and hiding it would make the
 * outcome unexplainable — and it is shown *only* here, phrased as recorded
 * years in that area, never as a career total and never outside the
 * requirement it belongs to.
 */
export function describeEmployeeValue(evaluation: RequirementEvaluationResponse): string | null {
  const { requirement, employeeValue } = evaluation;

  switch (requirement.subjectType) {
    case 'attribute':
      return employeeValue.attributeComposite === null
        ? null
        : `${employeeValue.attributeComposite.toFixed(1)} composite`;
    case 'skill':
      return employeeValue.skillProficiencyLevel === null
        ? null
        : PROFICIENCY_COPY[employeeValue.skillProficiencyLevel].label;
    case 'experience_area':
      return employeeValue.experienceYears === null
        ? null
        : `${employeeValue.experienceYears} recorded ${employeeValue.experienceYears === 1 ? 'year' : 'years'} in this area`;
    case 'qualification':
      if (!employeeValue.qualificationHeld) return null;
      return employeeValue.qualificationExpired ? 'Held, past its expiry date' : 'Held';
    case 'capability':
      return employeeValue.capabilityValue === null ? null : `${employeeValue.capabilityValue} computed`;
  }
}
